'use client';

import {Icon} from '@udemy-v2/icons';
import {useFormatDate, useI18n} from '@udemy-v2/i18n';
import {useFormatCurrency} from '@udemy-v2/react-currency-formatting';
import {DateIntervalType, useTeamSubscriptionPlan} from '@udemy-v2/subscription-graphql';

import {SALE_END_DATE} from '@/features/organization-team-plan/utils/team-plan-sale-window';

import styles from './team-plan-price-summary.module.scss';

/**
 * Annual per-seat Team Plan price shown inside the benefits card on the signup pages
 * (free-trial, buy-team-plan), so buyers see what the plan costs before filling in the form.
 *
 * @remarks
 * Identical on both routes: the discount treatment (struck-through original price, save badge,
 * offer end date) is driven entirely by whether the pricing service returns a discount.
 */
export function TeamPlanPriceSummary() {
    const {gettext, interpolate} = useI18n();
    const {toLocaleDateString} = useFormatDate();
    const {formatCurrency} = useFormatCurrency();
    const teamPlanContext = useTeamSubscriptionPlan();

    if (!teamPlanContext || teamPlanContext.isLoading || teamPlanContext.isSubscriber) {
        return null;
    }

    const discount = teamPlanContext.selectedDiscount;
    const annualOption = teamPlanContext.priceOption(DateIntervalType.Year);
    const listPrice = annualOption?.price?.list ?? annualOption?.listPrice?.amount;

    // Prefer adapter-formatted prices (plan currency from pricing) over re-formatting with
    // user.country, which defaults to USD for anonymous visitors. The undiscounted path has no
    // adapter-formatted string, so it must pass the plan's own currency explicitly.
    const currency = annualOption?.price?.currency ?? annualOption?.listPrice?.currency;
    const currentPrice =
        discount?.formattedSubscriptionDiscountPrice ??
        (listPrice == null
            ? undefined
            : formatCurrency(listPrice, {currency: currency ?? undefined}));

    if (!currentPrice) {
        return null;
    }

    const originalPrice = discount?.formattedSubscriptionNonDiscountPrice;
    // The adapter only populates this for PERCENTAGE discounts - fixed-amount discounts leave it
    // empty, in which case there is no percentage to advertise.
    const discountPercent = discount?.subscriptionDiscountPercent;

    return (
        <div className={styles.summary} data-testid="team-plan-price-summary">
            <p className={styles.name}>{gettext('Team Plan')}</p>
            <p className={styles['price-row']}>
                {originalPrice && <span className={styles['original-price']}>{originalPrice}</span>}
                <span className={styles['current-price']}>{currentPrice}</span>
                {discountPercent && (
                    <span className={styles['save-badge']}>
                        <Icon glyph="coupon" label={false} size="xsmall" color="inherit" />
                        {interpolate(
                            gettext('Save %(percent)s'),
                            {percent: `${discountPercent}%`},
                            true,
                        )}
                    </span>
                )}
            </p>
            <p className={styles.unit}>{gettext('per user / year plus applicable taxes')}</p>
            {discount && (
                <p className={styles['offer-ends']}>
                    <Icon glyph="timer" label={false} size="xsmall" color="inherit" />
                    {interpolate(
                        gettext('Offer ends %(endDate)s'),
                        {
                            endDate: toLocaleDateString(SALE_END_DATE, {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                            }),
                        },
                        true,
                    )}
                </p>
            )}
        </div>
    );
}
